<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fab fa-telegram"></i> Telegram Bot Setup
                        </h4>
                        <div class="action-btn">
                            <button @click="loadConfig" class="btn btn-light">
                                <i class="fas fa-sync"></i> Reload
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Status row -->
            <div class="row mb-3">
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Status</small>
                        <h4 class="mb-0" :class="config.enabled === 1 ? 'text-success' : 'text-secondary'">
                            <i :class="config.enabled === 1 ? 'fas fa-check-circle' : 'fas fa-pause-circle'"></i>
                            {{ config.enabled === 1 ? 'ENABLED' : 'DISABLED' }}
                        </h4>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Last Test</small>
                        <div><strong>{{ formatTime(config.last_test_at) }}</strong></div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Last Error</small>
                        <div :class="config.last_error_at ? 'text-danger' : 'text-muted'">
                            <strong>{{ formatTime(config.last_error_at) }}</strong>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Webhook Secret</small>
                        <div><code style="font-size:13px;">{{ config.webhook_secret_masked || '(none)' }}</code></div>
                    </div>
                </div>
            </div>

            <!-- Error banner -->
            <div v-if="config.last_error_message" class="alert alert-danger mb-3">
                <strong>Last Error:</strong> <code>{{ config.last_error_message }}</code>
            </div>

            <!-- Form -->
            <div class="card p-4 mb-3">
                <h5 class="mb-3"><i class="fas fa-cog"></i> Konfigurasi Bot</h5>
                <div class="row">
                    <div class="col-md-12 mb-3">
                        <label>Bot Token <small class="text-muted">(dari <a href="https://t.me/BotFather" target="_blank">@BotFather</a>)</small></label>
                        <input v-model="form.bot_token" type="password" class="form-control"
                            :placeholder="config.has_bot_token ? `(saved, currently: ${config.bot_token_masked} — leave kosong jika tidak ubah)` : 'paste bot token here'">
                        <small class="form-text text-muted">Format: <code>123456789:AABBccDDee...</code></small>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label>Chat ID <small class="text-muted">(id chat / group tujuan)</small></label>
                        <input v-model="form.chat_id" class="form-control" placeholder="-1001234567890 atau 123456789">
                        <small class="form-text text-muted">Untuk group: cari di <code>@getmyid_bot</code> setelah add bot ke group.</small>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label>Chat Label <small class="text-muted">(catatan internal)</small></label>
                        <input v-model="form.chat_label" class="form-control" placeholder="Tim Support StarSender">
                    </div>
                    <div class="col-md-12 mb-3">
                        <div class="form-check">
                            <input v-model="enabledBool" type="checkbox" class="form-check-input" id="enabledChk">
                            <label class="form-check-label" for="enabledChk">
                                <strong>Aktifkan bot</strong> — jika dimatikan, semua notif Telegram di-skip.
                            </label>
                        </div>
                    </div>
                </div>
                <div class="d-flex gap-2">
                    <button @click="saveConfig" :disabled="saving" class="btn btn-primary mr-2">
                        <i class="fas fa-save"></i> {{ saving ? 'Saving...' : 'Simpan Konfigurasi' }}
                    </button>
                    <button @click="testSend" :disabled="testing || !config.id" class="btn btn-info mr-2">
                        <i class="fas fa-paper-plane"></i> {{ testing ? 'Mengirim...' : 'Test Send' }}
                    </button>
                    <button @click="setWebhook" :disabled="settingHook || !config.id" class="btn btn-warning">
                        <i class="fas fa-link"></i> {{ settingHook ? 'Menyiapkan...' : 'Daftarkan Webhook' }}
                    </button>
                </div>
            </div>

            <!-- Webhook info -->
            <div class="card p-4 mb-3">
                <h5 class="mb-3"><i class="fas fa-broadcast-tower"></i> Webhook</h5>
                <p>
                    Webhook URL yang akan didaftarkan ke Telegram:<br>
                    <code style="user-select:all;">{{ webhookUrl }}</code>
                </p>
                <p class="text-muted small mb-0">
                    Telegram akan POST ke URL ini setiap kali ada pesan baru di chat yang ditarget,
                    sambil membawa header <code>X-Telegram-Bot-Api-Secret-Token</code> = webhook_secret.
                    Backend akan validasi header tersebut sebelum proses.
                </p>
            </div>

            <!-- Help / How to -->
            <div class="card p-4">
                <h5 class="mb-3"><i class="fas fa-question-circle"></i> Cara Setup</h5>
                <ol>
                    <li>Buka <a href="https://t.me/BotFather" target="_blank">@BotFather</a> di Telegram → <code>/newbot</code> → ikuti instruksi → copy <strong>HTTP API token</strong>.</li>
                    <li>Buat grup Telegram (atau pakai chat pribadi) → invite bot tadi sebagai member → jadikan admin agar bisa baca pesan.</li>
                    <li>Cari Chat ID grup tersebut: invite <a href="https://t.me/getmyid_bot" target="_blank">@getmyid_bot</a> ke grup, ia akan kirim Chat ID (biasanya angka negatif).</li>
                    <li>Isi <strong>Bot Token</strong> + <strong>Chat ID</strong> di form atas, centang <strong>Aktifkan</strong>, klik <strong>Simpan</strong>.</li>
                    <li>Klik <strong>Test Send</strong> — kalau pesan masuk ke chat berarti credentials benar.</li>
                    <li>Klik <strong>Daftarkan Webhook</strong> sekali — supaya Telegram tahu kemana harus kirim update.</li>
                    <li>Selesai. Sekarang setiap tiket baru/reply akan masuk ke chat tersebut. Untuk balas user, reply pesan tiket di chat Telegram.</li>
                    <li><strong>Monitor infra:</strong> jika bot aktif, StarSender mengirim alert ke chat ini saat domain/subdomain atau node server WA <em>offline</em> dan saat <em>online kembali</em> (menu Admin → Domain Monitor / Node Server Monitor).</li>
                </ol>
            </div>

        </div>
    </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';

export default {
    data() {
        return {
            saving: false,
            testing: false,
            settingHook: false,
            webhookUrl: 'https://api.starsender.online/api/telegram/webhook',
            config: {
                id: 0,
                enabled: 0,
                bot_token_masked: '',
                has_bot_token: false,
                chat_id: '',
                chat_label: '',
                webhook_secret_masked: '',
                last_test_at: null,
                last_error_at: null,
                last_error_message: '',
            },
            form: {
                bot_token: '',
                chat_id: '',
                chat_label: '',
                enabled: 0,
            },
        };
    },
    computed: {
        enabledBool: {
            get() { return this.form.enabled === 1; },
            set(v) { this.form.enabled = v ? 1 : 0; },
        },
    },
    mounted() {
        this.loadConfig();
    },
    methods: {
        notify(type, msg) {
            if (this.$toast) {
                this.$toast.open({ message: msg, type, duration: type === 'error' ? 6000 : 3000, position: 'top-right' });
            } else {
                // fallback
                window.alert(msg);
            }
        },
        errMsg(e, fallback) {
            return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || fallback;
        },
        async loadConfig() {
            try {
                const r = await axios.get('admin/telegram/config');
                if (r.data && r.data.success) {
                    this.config = r.data.data.config;
                    this.webhookUrl = r.data.data.webhook_url || this.webhookUrl;
                    // Pre-fill form (don't pre-fill bot_token — it's intentionally empty for security)
                    this.form.bot_token = '';
                    this.form.chat_id = this.config.chat_id || '';
                    this.form.chat_label = this.config.chat_label || '';
                    this.form.enabled = this.config.enabled || 0;
                }
            } catch (e) {
                this.notify('error', 'Gagal load config: ' + this.errMsg(e, 'unknown'));
            }
        },
        async saveConfig() {
            if (!this.form.chat_id) {
                this.notify('warning', 'Chat ID wajib diisi');
                return;
            }
            this.saving = true;
            try {
                const r = await axios.post('admin/telegram/config', this.form);
                if (r.data && r.data.success) {
                    this.notify('success', 'Konfigurasi tersimpan');
                    await this.loadConfig();
                } else {
                    this.notify('error', 'Gagal simpan: ' + ((r.data && r.data.message) || 'unknown'));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, 'unknown'));
            } finally {
                this.saving = false;
            }
        },
        async testSend() {
            this.testing = true;
            try {
                const r = await axios.post('admin/telegram/test');
                if (r.data && r.data.success) {
                    this.notify('success', 'Test message terkirim — cek chat Telegram');
                    await this.loadConfig();
                } else {
                    this.notify('error', 'Test gagal: ' + ((r.data && r.data.message) || 'unknown'));
                }
            } catch (e) {
                this.notify('error', 'Test gagal: ' + this.errMsg(e, 'unknown'));
                await this.loadConfig();
            } finally {
                this.testing = false;
            }
        },
        async setWebhook() {
            this.settingHook = true;
            try {
                const r = await axios.post('admin/telegram/set-webhook', { webhook_url: this.webhookUrl });
                if (r.data && r.data.success) {
                    this.notify('success', 'Webhook terdaftar di Telegram');
                } else {
                    this.notify('error', 'Gagal: ' + ((r.data && r.data.message) || 'unknown'));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + this.errMsg(e, 'unknown'));
            } finally {
                this.settingHook = false;
            }
        },
        formatTime(ms) {
            if (!ms) return '—';
            return moment(parseInt(ms)).format('DD MMM YYYY HH:mm:ss');
        },
    },
};
</script>

<style scoped>
.gap-2 > * + * { margin-left: 8px; }
</style>
